/**
 * Tabela genérica usada nas páginas CRUD.
 *
 *   <DataTable
 *     data={rows}
 *     columns={[
 *       { key: "name", header: "Nome", cell: r => r.name },
 *       { key: "actions", header: "", cell: r => <Button …/>, align: "right" },
 *     ]}
 *   />
 */

import clsx from "clsx";
import type { ReactNode } from "react";
import { Skeleton } from "./Skeleton";
import { EmptyState } from "./EmptyState";

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  align?: "left" | "center" | "right";
  className?: string;
  /** Esconde em mobile. */
  hideOnMobile?: boolean;
}

interface Props<T> {
  data: T[] | undefined;
  columns: Column<T>[];
  loading?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** rowKey que recebe destaque visual + data-row-focus="true" pro scroll. */
  highlightedRowKey?: string;
}

export function DataTable<T>({
  data,
  columns,
  loading,
  emptyTitle = "Nada por aqui ainda",
  emptyDescription,
  rowKey,
  onRowClick,
  highlightedRowKey,
}: Props<T>) {
  if (loading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  // `Array.isArray` e não `!data`: a PWA ativa o bundle novo só no próximo
  // carregamento, então existe uma janela em que JS ANTIGO conversa com API
  // NOVA. Em 17/09 isso derrubou a tela de ocorrências inteira com
  // "e.map is not a function" quando a resposta virou envelope — e tela em
  // branco por causa de formato é o pior desfecho possível: some tudo, não
  // só a lista. Lista vazia é ruim; tela morta é pior.
  const linhas = Array.isArray(data) ? data : [];

  if (linhas.length === 0) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-surface-200 dark:border-slate-800 bg-white dark:bg-slate-900">
      <table className="min-w-full divide-y divide-surface-200 dark:divide-slate-800 text-sm">
        <thead className="bg-surface-50 dark:bg-slate-800/40">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={clsx(
                  "px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400",
                  col.align === "right" && "text-right",
                  col.align === "center" && "text-center",
                  col.align === undefined && "text-left",
                  col.hideOnMobile && "hidden sm:table-cell",
                )}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-surface-100 dark:divide-slate-800 bg-white dark:bg-slate-900">
          {linhas.map((row) => {
            const key = rowKey(row);
            const isHighlighted = highlightedRowKey === key;
            return (
            <tr
              key={key}
              data-row-key={key}
              data-row-focus={isHighlighted ? "true" : undefined}
              className={clsx(
                "transition-colors",
                onRowClick && "cursor-pointer hover:bg-surface-50 dark:hover:bg-slate-800/50",
                isHighlighted && "bg-amber-50 dark:bg-amber-900/20 ring-2 ring-amber-300 ring-inset animate-fade-in",
              )}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={clsx(
                    "px-4 py-3 text-slate-700 dark:text-slate-300",
                    col.align === "right" && "text-right",
                    col.align === "center" && "text-center",
                    col.hideOnMobile && "hidden sm:table-cell",
                    col.className,
                  )}
                  onClick={
                    col.key === "actions" ? (e) => e.stopPropagation() : undefined
                  }
                >
                  {col.cell(row)}
                </td>
              ))}
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
